import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ErrorModal, Loading } from "components";
import { FixedAssignmentsFinishedModal } from "components/Modal/Tutoring/FixedAssignmentsFinished";
import TutoringBar from "features/HeaderBar/TutoringBar";
import EvaluationScreen from "features/Tutoring/Evaluation";
import PracticeScreen from "features/Tutoring/Practice";
import WelcomeScreen from "features/Tutoring/Welcome";

import type { UserAssignmentDetailFragment, UserTutoringPracticeByIdFragment } from "api/generated";
import {
  useGetTutoringPracticeByIdQuery,
  useTutoringClaimEvaluationMutation,
  useTutoringResetMutation,
} from "api/generated";

import { TutoringProvider } from "features/AssignmentTypes/Tutoring/context/TutoringContext";
import { useAppSelector } from "store/hooks";
import { getUserEmail } from "store/slices/user";
import { resetTime } from "utils/common/video";
import { processApiError } from "utils/processApiError";

interface AssignmentsData {
  userAssignmentObject?:
    | {
        id: string;
      }
    | null
    | undefined;
  finished: boolean;
  type: "fixed" | "other";
}

const TutoringPractice = () => {
  const { tutoringId, subjectSlug } = useParams();
  const navigate = useNavigate();

  const {
    data,
    isLoading: isTutoringLoading,
    error: apiError,
  } = useGetTutoringPracticeByIdQuery(
    { tutoringId: tutoringId ?? "" },
    { skip: !tutoringId, refetchOnMountOrArgChange: true },
  );
  const [claimEvaluationTrigger, { error: apiEvaluationError }] = useTutoringClaimEvaluationMutation();

  const [tutoringData, setTutoringData] = useState<UserTutoringPracticeByIdFragment | null>(null);
  const [themeName, setThemeName] = useState<string | undefined>(undefined);
  const [step, setStep] = useState<"welcome" | "practice" | "evaluation">("welcome");
  const [assignmentsList, setAssignmentsList] = useState<AssignmentsData[]>([]);
  const [assignmentDetails, setAssignmentDetails] = useState<UserAssignmentDetailFragment[]>([]);
  const [activeAssignment, setActiveAssignment] = useState<UserAssignmentDetailFragment | undefined>(undefined);
  const [resetTutoring, { isLoading: isResetLoading }] = useTutoringResetMutation();
  const [isShownFixedFinishedModal, showFixedFinishedModal] = useState(false);
  const [isErrorModalVisible, setErrorModalVisible] = useState(false);
  const userEmail = useAppSelector(getUserEmail);

  // Nad časným návratem, ať nepřidává další podmíněný hook.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [activeAssignment?.id]);

  if (!tutoringId) {
    navigate("/");
    return null;
  }

  useEffect(() => {
    if (apiError === undefined) return;

    processApiError(apiError);
  }, [apiError]);

  useEffect(() => {
    if (apiEvaluationError === undefined) return;

    processApiError(apiEvaluationError);
    setErrorModalVisible(true);
  }, [apiEvaluationError]);

  const claimEvaluation = async ({
    tutoringId,
    userAssignmentId,
    userAnswers,
  }: {
    tutoringId: string;
    userAssignmentId: string;
    userAnswers: boolean | boolean[];
  }): Promise<void> => {
    const currentAssignmentId = activeAssignment?.id;
    const currentAssignmentData = assignmentsList.find(
      assignment => assignment.userAssignmentObject?.id === currentAssignmentId,
    );
    if (currentAssignmentData) currentAssignmentData.finished = true;

    if (!tutoringData?.userAchievements.length) {
      const fixed = assignmentsList.filter(assignment => assignment.type === "fixed");
      const areAllFixedFinished = fixed.every(assignment => assignment.finished);
      if (areAllFixedFinished && currentAssignmentData?.type === "fixed") {
        showFixedFinishedModal(true);
      }
    }

    await claimEvaluationTrigger({ tutoringId, userAssignmentId, userAnswers });
  };

  const processTutoringData = (list: UserTutoringPracticeByIdFragment, videoTips: Array<{ videoId: string }>) => {
    setTutoringData(list);

    const details = [
      ...list.themeAssignments.map(a => a.userAssignmentObject),
      ...list.assignments.map(a => a.userAssignmentObject),
    ].filter((a): a is UserAssignmentDetailFragment => a != null);
    setAssignmentDetails(details);

    const themeAssignments = list.themeAssignments
      .map(assignment => ({
        ...assignment,
        type: "fixed" as const,
      }))
      .sort((a, b) => {
        const aRate = a.userAssignmentObject?.assignment?.successRate ?? -1;
        const bRate = b.userAssignmentObject?.assignment?.successRate ?? -1;
        return bRate - aRate;
      });

    const otherAssignments = list.assignments.map(assignment => ({
      ...assignment,
      type: "other" as const,
    }));

    const allAssignments = [...themeAssignments, ...otherAssignments];
    const validAssignmentsList = allAssignments.filter(assignment => !!assignment.userAssignmentObject);
    const finishedAssignments = validAssignmentsList.filter(assignment => assignment.finished);

    if (finishedAssignments.length >= validAssignmentsList.length) {
      resetTutoring({ tutoringId }).catch(() => {});
      if (videoTips.length && userEmail)
        resetTime(
          videoTips.map(tip => tip.videoId),
          userEmail,
        );
      setAssignmentsList(validAssignmentsList.map(assignment => ({ ...assignment, finished: false })));
    } else {
      setAssignmentsList(validAssignmentsList);
    }
  };

  useEffect(() => {
    if (!data) return;

    const tutoring = data.userTutoringById;
    const themeDetail = tutoring.themeDetail;
    const videoTips = themeDetail?.video ?? [];

    setThemeName(themeDetail ? (themeDetail.appName ?? themeDetail.name ?? "") : undefined);
    processTutoringData(tutoring, videoTips);
  }, [data]);

  const changeStep = (newStep: "practice" | "evaluation") => {
    setStep(newStep);
  };

  const onQuit = () => {
    subjectSlug ? navigate(`/doucovani/${subjectSlug}`) : navigate("/");
  };

  const changeAssignment = () => {
    const newAssignmentId = assignmentsList.find(assignment => !assignment.finished)?.userAssignmentObject?.id;

    if (!newAssignmentId) {
      changeStep("evaluation");
      return;
    }

    const assignmentDetail = assignmentDetails.find(a => a.id === newAssignmentId);
    setActiveAssignment(assignmentDetail);
  };

  // AIDEV-NOTE: Schválně `isLoading`, ne `isFetching`. Dotaz má `refetchOnMountOrArgChange`,
  // takže `isFetching` je pravdivé i při návratu na už načtené doučko — spinner by pak zakryl
  // data, která student má v ruce. `isLoading` je pravdivé jen při prvním načtení bez dat.
  if (isTutoringLoading || isResetLoading) return <Loading />;

  if (step === "evaluation") return <EvaluationScreen />;

  if (themeName && (step === "practice" || step === "welcome")) {
    return (
      <div className="container">
        <div className="tutoring full-page">
          <div className="tutoring__header">
            <TutoringBar
              userAssignmentId={activeAssignment?.id}
              themeName={themeName}
              videoTips={data?.userTutoringById.themeDetail?.video ?? []}
              assignmentsData={assignmentsList}
              onQuit={onQuit}
              hideSoundToggle={step === "welcome"}
            />
          </div>
          {step === "practice" ? (
            <TutoringProvider
              themeName={themeName}
              tutoringId={tutoringId}
              videoTips={data?.userTutoringById.themeDetail?.video ?? []}
              claimEvaluation={claimEvaluation}
            >
              <PracticeScreen activeAssignment={activeAssignment} changeAssignment={changeAssignment} />
            </TutoringProvider>
          ) : null}
          {step === "welcome" && tutoringData && themeName ? (
            <WelcomeScreen
              tutoring={tutoringData}
              goNextStep={changeStep}
              videoTips={data?.userTutoringById.themeDetail?.video ?? []}
            />
          ) : null}
        </div>

        {isShownFixedFinishedModal && (
          <FixedAssignmentsFinishedModal
            onClose={() => {
              showFixedFinishedModal(false);
            }}
          />
        )}

        <ErrorModal
          onCancel={() => {
            setErrorModalVisible(false);
          }}
          onHide={() => {
            setErrorModalVisible(false);
          }}
          onRetry={() => {}}
          show={isErrorModalVisible}
        />
      </div>
    );
  }

  return (
    <div className="container">
      <div>Nepodařilo se načíst data</div>
    </div>
  );
};

export default TutoringPractice;
